import React from 'react'
import {
  DataSource,
  SingleValueDataKeySpecificProps,
} from '../../../../../shared/models/panel'
import {
  AggregatedTimeseriesData,
  LatestTimeseriesDataWithPrevious,
} from '../../../../../shared/models/telemetry/telemetry.model'
import { TimeWindow } from '../../../../../shared/models/time.model'
import { Aliases } from '../../../../../shared/models/alias.model'
import { ChartProps, Pie } from 'react-chartjs-2'
import _ from 'lodash'
import { useAppSelector } from '../../../../../store/store'
import { ChartData, Plugin } from 'chart.js'
import { afterDatasetDrawPiChart } from './utils'

interface PieChartProps {
  aggregateTimeSeriesData?: {
    [key: string]: {
      datasource: DataSource<SingleValueDataKeySpecificProps>
      data: AggregatedTimeseriesData
    }
  }
  latestTimeSeriesData?: {
    [key: string]: {
      datasource: DataSource<SingleValueDataKeySpecificProps>
      data: LatestTimeseriesDataWithPrevious
    }
  }
  timeWindow?: TimeWindow
  aliases: Aliases
  options?: ChartProps<'pie'>['options']
  specificOptions?: any
}

const newShade = (hexColor: string, magnitude: number) => {
  hexColor = hexColor.replace(`#`, ``)
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16)
    let r = (decimalColor >> 16) + magnitude
    r > 255 && (r = 255)
    r < 0 && (r = 0)
    let g = (decimalColor & 0x0000ff) + magnitude
    g > 255 && (g = 255)
    g < 0 && (g = 0)
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude
    b > 255 && (b = 255)
    b < 0 && (b = 0)
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`
  } else {
    return hexColor
  }
}

const TimeSeriesPieChart = ({
  aggregateTimeSeriesData,
  latestTimeSeriesData,
  timeWindow,
  aliases,
  options,
  specificOptions,
}: PieChartProps) => {
  const [chartData, setChartData] = React.useState<ChartData<'pie', unknown[]>>(
    {
      labels: [],
      datasets: [
        {
          data: [],
        },
      ],
    }
  )
  const { theme } = useAppSelector((state) => state.settings)
  React.useEffect(() => {
    let config: { color: string; label: string; value: unknown }[] = []
    if (aggregateTimeSeriesData) {
      config.push(
        ..._.chain(aggregateTimeSeriesData)
          .flatMap((v) =>
            v.datasource.dataKeys.map((dataKey) => ({ dataKey, data: v.data }))
          )
          .map(({ dataKey, data }, i) => {
            let value = 0
            if (dataKey.key in data) {
              value = data[dataKey.key].current?.value || 0
            }
            return {
              color: dataKey.color ? dataKey.color : 'red',
              label: dataKey.label ? dataKey.label : dataKey.key,
              value,
            }
          })
          .value()
      )
    }
    setChartData({
      labels: config.map((c) => c.label),
      datasets: [
        {
          data: config.map((c) => c.value),
          backgroundColor: config.map((c) => c.color),
          hoverOffset: 20,
        },
      ],
    })
  }, [aggregateTimeSeriesData])
  const showValuePlugin: Plugin<'pie', any> = {
    id: 'showValuePlugin',
    afterDatasetDraw: afterDatasetDrawPiChart,
  }
  return (
    <>
      <Pie
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          hover: {
            mode: 'index',
          },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                font: {
                  size: 14,
                },
                color: theme === 'dark' ? 'white' : 'black',
              },
            },
          },
        }}
        plugins={[showValuePlugin]}
      />
    </>
  )
}

export default TimeSeriesPieChart
